import { useInfiniteQuery } from '@tanstack/react-query';
import { User } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRestClient } from '../../../core/api/RestClientContext';
import { Button, RefreshBtn } from '../../../core/components/Button';
import { Card } from '../../../core/components/Card';
import { useInfiniteScroll } from '../../../core/hooks/useInfiniteScroll';
import { UsersFilterShape } from '../../../core/models/user';
import XuiExportDialog from '../../xui/components/XuiExportDialog';
import { FilterSummary } from './FilterSummary';
import './UsersList.css';

interface UsersListProps {
    className?: string;
    filter?: UsersFilterShape;
}

export const UsersList: React.FC<UsersListProps> = ({ className = '', filter = {
    hasSubscription: true
} }) => {
    const navigate = useNavigate();
    const restClient = useRestClient();
    const [showXuiExport, setShowXuiExport] = useState(false);

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError,
        refetch,
    } = useInfiniteQuery({
        queryKey: ['users', filter],
        queryFn: ({ pageParam }) => restClient.getUsersPaginated({
            page: pageParam,
            pageSize: 20,
            filter,
        }),
        initialPageParam: 0,
        getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.page + 1 : undefined,
    });

    const users = useMemo(() => data?.pages.flatMap(p => p.data) ?? [], [data]);
    const total = data?.pages[0]?.total ?? 0;

    const loadMore = useCallback(() => {
        if (!isFetchingNextPage && hasNextPage) {
            fetchNextPage();
        }
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

    const lastElementRef = useInfiniteScroll({
        loading: isFetchingNextPage,
        hasMore: hasNextPage ?? false,
        onLoadMore: loadMore,
    });

    const handleUserClick = useCallback((userId: string) => {
        navigate('/users/' + userId);
    }, [navigate]);

    const handleRefresh = useCallback(() => { refetch(); }, [refetch]);

    if (isLoading) {
        return (
            <div className={`users-list ${className}`}>
                <div className="users-list-header">
                    <div>
                        <h3>Пользователи</h3>
                        <RefreshBtn onClick={handleRefresh} disabled />
                    </div>
                    <div className="loading">Загрузка пользователей...</div>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className={`users-list ${className}`}>
                <div className="users-list-header">
                    <div>
                        <h3>Пользователи</h3>
                        <RefreshBtn onClick={handleRefresh} showLabel />
                    </div>
                    <div className="error">Ошибка загрузки пользователей</div>
                </div>
            </div>
        );
    }

    if (showXuiExport)
        return <XuiExportDialog onClose={() => setShowXuiExport(false)} />

    return (
        <Card className={`users-list ${className}`}>
            <div className="users-list-header">
                <div>
                    <h3>Пользователи ({total})</h3>
                    {users.length > 0 && total > 0 && (
                        <div className="loading-progress">
                            <span>Загружено: {users.length} из {total}</span>
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${(users.length / total) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <Button variant="secondary" onClick={() => setShowXuiExport(true)}>
                    Экспорт XUI (JSON)
                </Button>
                <div className="users-list-actions">
                    <RefreshBtn onClick={handleRefresh} />
                </div>
            </div>

            <FilterSummary filter={filter} />

            <div className="users-list-content">
                {users.length === 0 ? (
                    <div className="no-users">Пользователи не найдены</div>
                ) : (
                    <div className="users-grid">
                        {users.map((user, index) => {
                            const isLastElement = index === users.length - 1;
                            return (
                                <div
                                    key={user.id}
                                    className="user-card"
                                    role="button"
                                    tabIndex={0}
                                    ref={isLastElement ? lastElementRef : undefined}
                                    onClick={() => handleUserClick(user.id)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleUserClick(user.id); }}
                                >
                                    <div className="user-avatar">
                                        <User style={{ color: 'var(--color-text)' }} />
                                    </div>
                                    <div className="user-info">
                                        <div className="user-name">{user.tgName}</div>
                                        <div className="user-id">TG_ID: {user.tgId}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {isFetchingNextPage && (
                    <div className="loading-more">
                        <div className="loading-spinner">Загрузка...</div>
                    </div>
                )}
                {!hasNextPage && users.length > 0 && (
                    <div className="no-more-users">
                        Все пользователи загружены
                    </div>
                )}
            </div>
        </Card>
    );
};

export default UsersList;
